import { Money } from '@klear/money'
import { useForm } from 'react-hook-form'
import FormInput from '@klear/ui/form/input'
import FormMoneyInput from '@klear/ui/form/money-input'
import { Alert, AlertDescription, AlertTitle } from '@klear/ui/components/alert'
import {
  useTraderExternalBankAccount,
  useTraderExternalBankAccountMutations
} from '@klear/ui/hooks/use-trader-external-bank-accounts'
import { TraderExternalBankAccount } from '@klear/trader-external-bank-account'
import { EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_FAILED } from '@klear/bank-account/constants'
import {
  hasVerifyMicrodepositsFormErrors,
  MICRODEPOSIT_MAX,
  validateVerifyMicrodepositsForm,
  verifyMicrodepositsFormFieldErrors,
  type VerifyMicrodepositsForm
} from '@klear/trader-external-bank-account/verify-microdeposits.schema'
import {
  userFacingVerificationErrorMessage,
  VERIFICATION_CANCELED_FALLBACK,
  VERIFICATION_EXHAUSTED_ATTEMPTS_FALLBACK,
  VERIFICATION_UNAVAILABLE_FALLBACK
} from '@klear/trader-external-bank-account/verification-error-message'
import { Modal } from '@klear/ui/components/modal'
import { useEffect, useRef, useState, useMemo } from 'react'
import { trpc, type RouterOutputs } from '@klear/ui/lib/trpc'
import LoadingSpinner from '@klear/ui/components/spinner'
import { GeneratedLegalAgreementViewer } from '@/app/legal-agreement/generated-legal-agreement-viewer'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useTraderLegalAgreements } from '@/app/legal-agreement/use-legal-agreement'
import isError from 'lodash/isError'

type VerificationMethodQueryData = RouterOutputs['traderExternalBankAccount']['getVerificationMethod']

interface VerifyExternalBankAccountProps {
  externalBankAccount: TraderExternalBankAccount
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function VerifyExternalBankAccount({
  externalBankAccount: initialExternalBankAccount,
  open,
  onOpenChange
}: VerifyExternalBankAccountProps) {
  const { verifyMicrodeposits, isVerifying } = useTraderExternalBankAccountMutations()
  const [error, setError] = useState<string | null>(null)
  const [accountIdForAch, setAccountIdForAch] = useState<number | null>(null)
  const { user } = useAuth()
  const traderId = user?.traderIds[0] || 1
  const { invalidateTraderLegalAgreements } = useTraderLegalAgreements()
  const utils = trpc.useUtils()

  const accountId = initialExternalBankAccount.id ?? 0
  const { traderExternalBankAccount: freshAccount } = useTraderExternalBankAccount(accountId, { enabled: open })
  const externalBankAccount = freshAccount ?? initialExternalBankAccount

  const canFetchVerificationMethod =
    externalBankAccount.canVerify ||
    (externalBankAccount.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_FAILED &&
      !!externalBankAccount.stripeSetupIntentId)

  const refreshVerificationStatus = trpc.traderExternalBankAccount.refreshVerificationStatus.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.traderExternalBankAccount.get.invalidate({ id: accountId }),
        utils.traderExternalBankAccount.list.invalidate()
      ])
    }
  })
  const hasRefreshedForOpenRef = useRef(false)

  useEffect(() => {
    if (!open) {
      hasRefreshedForOpenRef.current = false
      return
    }
    if (accountId === 0 || !externalBankAccount.stripeSetupIntentId || hasRefreshedForOpenRef.current) return
    hasRefreshedForOpenRef.current = true
    refreshVerificationStatus.mutate({ id: accountId })
  }, [open, accountId, externalBankAccount.stripeSetupIntentId, refreshVerificationStatus.mutate])

  const { data, isLoading: isLoadingVerificationMethod } =
    trpc.traderExternalBankAccount.getVerificationMethod.useQuery(
      { id: accountId },
      { enabled: open && canFetchVerificationMethod && accountId > 0 }
    )
  const verificationData: VerificationMethodQueryData | undefined = data
  const verificationMethod = verificationData?.method ?? null
  const verificationMethodRef = useRef(verificationMethod)
  verificationMethodRef.current = verificationMethod
  const verificationStatus = verificationData?.status
  const verificationError = verificationData?.error
  const requiresSupport = verificationError?.requiresSupport === true

  const formValues = useMemo((): VerifyMicrodepositsForm => {
    if (!verificationMethod) {
      return { amount1: Money.zero(), amount2: Money.zero(), descriptorCode: '' }
    }
    return {
      amount1: verificationMethod === 'amounts' ? Money.zero() : undefined,
      amount2: verificationMethod === 'amounts' ? Money.zero() : undefined,
      descriptorCode: verificationMethod === 'descriptor_code' ? '' : undefined
    }
  }, [verificationMethod])

  const form = useForm<VerifyMicrodepositsForm>({
    resolver: async (values) => {
      const errors = validateVerifyMicrodepositsForm(values, verificationMethodRef.current)
      if (hasVerifyMicrodepositsFormErrors(errors)) {
        return { values: {}, errors: verifyMicrodepositsFormFieldErrors(errors) }
      }
      return { values, errors: {} }
    },
    values: formValues
  })

  const remainingAttempts = externalBankAccount.getRemainingAttempts(verificationMethod)

  const onSubmit = async (data: VerifyMicrodepositsForm) => {
    try {
      setError(null)
      if (remainingAttempts === 0) {
        setError(userFacingVerificationErrorMessage(null, VERIFICATION_EXHAUSTED_ATTEMPTS_FALLBACK))
        return
      }
      const amounts: [string, string] | undefined =
        data.amount1 !== undefined && data.amount2 !== undefined
          ? [data.amount1.toJSON(), data.amount2.toJSON()]
          : undefined
      const descriptorCode: string | undefined = data.descriptorCode?.trim() || undefined

      if (accountId === 0) return
      await verifyMicrodeposits({ id: accountId, amounts, descriptorCode })

      await invalidateTraderLegalAgreements()
      setTimeout(() => setAccountIdForAch(accountId), 500)

      onOpenChange(false)
      form.reset()
    } catch (err) {
      setError(isError(err) ? err.message : userFacingVerificationErrorMessage(null, 'Failed to verify micro-deposits'))
      await utils.traderExternalBankAccount.get.invalidate({ id: accountId })
    }
  }

  const showAmounts = verificationMethod === 'amounts' || !verificationMethod
  const showDescriptorCode = verificationMethod === 'descriptor_code' || !verificationMethod

  return (
    <>
      <Modal
        title="Verify Bank Account"
        description={
          verificationMethod === 'amounts'
            ? 'Enter the two micro-deposit amounts that were deposited into your bank account. These amounts are typically between $0.01 and $0.99.'
            : verificationMethod === 'descriptor_code'
              ? 'Enter the descriptor code from the microdeposit on your bank statement. The code starts with "SM" and appears on a $0.01 deposit.'
              : verificationStatus === 'succeeded'
                ? 'Your bank account has been verified.'
                : 'Enter the verification information from your bank statement.'
        }
        form={form}
        onSubmit={onSubmit}
        open={open}
        onOpenChange={onOpenChange}
        primaryText="Verify"
        primaryDisabled={isLoadingVerificationMethod || remainingAttempts === 0 || isVerifying || requiresSupport}
        size="sm"
      >
        <div className="space-y-4 mt-4">
          {requiresSupport && (
            <Alert variant="destructive">
              <AlertTitle>Verification Unavailable</AlertTitle>
              <AlertDescription>
                {userFacingVerificationErrorMessage(verificationError?.message, VERIFICATION_UNAVAILABLE_FALLBACK)}
              </AlertDescription>
            </Alert>
          )}

          {!requiresSupport && verificationStatus !== 'succeeded' && verificationStatus !== 'canceled' && (
            <>
              {isLoadingVerificationMethod ? (
                <div className="py-8">
                  <LoadingSpinner className="min-h-0! h-auto" />
                </div>
              ) : (
                <>
                  {error && (
                    <Alert variant="destructive">
                      <AlertTitle>Verification Failed</AlertTitle>
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {remainingAttempts === 0 && (
                    <Alert variant="destructive">
                      <AlertTitle>No Attempts Remaining</AlertTitle>
                      <AlertDescription>
                        {userFacingVerificationErrorMessage(null, VERIFICATION_EXHAUSTED_ATTEMPTS_FALLBACK)}
                      </AlertDescription>
                    </Alert>
                  )}
                  {remainingAttempts > 0 && remainingAttempts < (verificationMethod === 'descriptor_code' ? 10 : 3) && (
                    <Alert>
                      <AlertTitle>Remaining Attempts</AlertTitle>
                      <AlertDescription>
                        You have {remainingAttempts} {remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-4">
                    {showAmounts && (
                      <div className="grid grid-cols-2 gap-4">
                        <FormMoneyInput label="First Micro-Deposit Amount" name="amount1" max={MICRODEPOSIT_MAX} />
                        <FormMoneyInput label="Second Micro-Deposit Amount" name="amount2" max={MICRODEPOSIT_MAX} />
                      </div>
                    )}
                    {showDescriptorCode && (
                      <FormInput label="Descriptor Code" name="descriptorCode" type="text" placeholder="e.g., SM11AA" />
                    )}
                  </div>

                  <div className="text-sm text-muted-foreground">
                    {verificationMethod === 'amounts' && (
                      <p>
                        Check your bank statement for two small deposits (typically between $0.01 and $0.99). Enter the
                        exact amounts to verify your account.
                      </p>
                    )}
                    {verificationMethod === 'descriptor_code' && (
                      <p>
                        Check your bank statement for a $0.01 deposit with a descriptor code starting with
                        &quot;SM&quot;. Enter the complete code to verify your account.
                      </p>
                    )}
                    {!verificationMethod && (
                      <p>
                        Check your bank statement for the verification information. You will receive either two
                        microdeposit amounts or a descriptor code.
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          )}
          {verificationStatus === 'succeeded' && (
            <p>
              Your bank account has been verified. You may need to refresh the page to see the updated verification
              status.
            </p>
          )}
          {verificationStatus === 'canceled' && (
            <Alert variant="destructive">
              <AlertTitle>Bank Account Verification was Canceled</AlertTitle>
              <AlertDescription>
                {userFacingVerificationErrorMessage(null, VERIFICATION_CANCELED_FALLBACK)}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </Modal>

      <GeneratedLegalAgreementViewer
        traderId={traderId}
        open={accountIdForAch != null}
        onClose={() => setAccountIdForAch(null)}
        onSuccess={() => setAccountIdForAch(null)}
        externalAccountIds={accountIdForAch != null ? [accountIdForAch] : []}
        klearAccountIds={[]}
        accountId={accountIdForAch ?? undefined}
        accountType="external"
      />
    </>
  )
}
